import React, { useCallback, useEffect, useState } from "react";
import { State } from "./interfaces";
import EditEmployeeSurvey from "../EditEmployeeSurvey/EditEmployeeSurvey";
import MySurveyCard from "./MySurveysCard/MySurveysCard";
import { EmployeeSurveysApiService } from "../../services/employee-surveys/employee-surveys-api.service";
import { EmployeeSurveyFilter } from "../../models/employee-survey/employee-survey-filter";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { EmployeesApiService } from "../../services/employees/employees-api.service";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../custom-hooks/useWindowSize/useWindowSize";
import { SPACING } from "../../_globalStore/redux/reducers/Theme/themeConstants";
import { CUSTOM_PAGINATION_ID } from "../../reusable-components/Table/CustomTablePagination/utils";
import { Container } from "./styles";
import { Grid, Typography } from "@mui/material";
import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import TableHeader from "../TableHeader/TableHeader";
import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import useAbortableLoadResponse from "../../custom-hooks/useAbortableLoadResponse/useAbortableLoadResponse";
import { EmployeeDTO } from "../../models/employees";
import { EmployeeSurveyConfigurationStatuses, EmployeeSurveyStatuses } from "../../utils/constants/constants";

const MySurveysContainer = () => {
  const { t } = useTranslation();
  const tRP = "components.my_surveys_container";

  const INITIAL_STATE: State = {
    loading: false,
    error: "",
    items: [],
    selectedEmployee: null,
  };

  const [state, setState] = useState<State>({ ...INITIAL_STATE });

  const { height } = useWindowSize();
  const { searchOptions, handleChangeSearchOptions } = useSearchOptions("");

  //#region load items
  const loadData = useCallback(async () => {
    try {
      setState((state) => ({ ...state, loading: true }));
      const response = await new EmployeeSurveysApiService().loadItems({
        employeeIds: [state.selectedEmployee.id],
      } as EmployeeSurveyFilter);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState((state) => ({ ...state, items: response.items, error: "" }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.selectedEmployee?.id]);
  //#endregion

  useEffect(() => {
    if (state.selectedEmployee) {
      loadData();
    }
  }, [state.selectedEmployee, searchOptions.exactMatch, searchOptions.query]);

  const { response } = useAbortableLoadResponse<EmployeeDTO>(EmployeesApiService, {}, "/GetCurrentUserEmployees");

  useEffect(() => {
    if (response?.items) setState((state) => ({ ...state, selectedEmployee: response?.items[0] ?? null }));
  }, [response?.items]);

  const currHeight =
    height -
    (75 + //add button height
      SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById(`${CUSTOM_PAGINATION_ID}`)?.clientHeight ?? 0) +
      40);

  return (
    <Container>
      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        quickFilters={
          <QuickfiltersContainer>
            <AutocompleteDropdown
              labelKey={`${t(`common_words.employee`)}`}
              selectedValue={state.selectedEmployee}
              serviceApi={new EmployeesApiService()}
              apiLoadItemsPath={"/GetCurrentUserEmployees"}
              field="selectedEmployee"
              childField="legalEntity"
              childFieldProp="name"
              handleChange={(_, value) => {
                setState((state) => ({ ...state, selectedEmployee: value }));
              }}
              quickFilterLike
              inputStyle={{
                width: "250px",
                border: "none",
              }}
            />
          </QuickfiltersContainer>
        }
        tableTitle={`${t(`${tRP}.title`)}`}
      />
      <LoadingOverlay
        error={state.error}
        loading={state.loading}
      >
        {!state.loading && state.items.length === 0 ? (
          <Grid style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
            <Typography>{`${t(`common_words.no_items`)}`}</Typography>
          </Grid>
        ) : (
          <Grid
            sx={{
              width: "100%",
              height: `${currHeight}px`,
              overflow: "auto",
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {state.items.map((item) => {
              return (
                <EditEmployeeSurvey
                  key={item.id}
                  item={item}
                  handleNotify={loadData}
                >
                  <MySurveyCard item={item} />
                </EditEmployeeSurvey>
              );
            })}
          </Grid>
        )}
      </LoadingOverlay>
    </Container>
  );
};

export default MySurveysContainer;
